import React, { useEffect } from 'react'
import { useDropzone } from 'react-dropzone'
import './PhotoManager.sass'
import { concat, isEmpty, cloneDeep } from 'lodash'
import arrayMove from 'array-move'
import PhotoTile from './PhotoTile'
import promiseAllValues from '../../utils/promiseAllValues'
import SortableList from '../../ui/SortableList/SortableList'
import { photoObject, deletePhoto, setAvatar } from './functions'
import useCallbacks from '../../hooks/useCallbacks'
import { base64ToFile } from '../../utils/files/base64ToFile'
import useModuleInit from '../../utils/newforms/changed/useModuleInit'

function PhotoManager({
  projectId,
  projectPath,
  formData,
  propName = 'photos',
  syncState,
  persistDeletes = true,
  compact = false,
}) {
  const [photos, setPhotos] = useModuleInit({
    formData,
    initialState: [],
  })

  useEffect(() => {
    syncState(photos)
    // Parent components often pass an inline syncState callback.
    // Re-running on callback identity changes causes a render loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos])

  const handlers = useCallbacks((callbacks) => {
    callbacks.onDrop = (acceptedFiles) => {
      Promise.all(
        acceptedFiles.map((f, i) =>
          promiseAllValues(
            new photoObject({
              file: f,
              isAvatar: !!(i === 0 && isEmpty(photos)),
            })
          )
        )
      ).then((photoObjects) => setPhotos(concat(photos, photoObjects)))
    }
  })

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handlers.onDrop,
    multiple: true,
    accept: 'image/*',
  })

  const onSortEnd = ({ oldIndex, newIndex }) => {
    const newPhotos = arrayMove(photos, oldIndex, newIndex)
    setPhotos(newPhotos)
  }

  const onImageEdited = (newDataUrl, photoPos) => {
    const photosClone = cloneDeep(photos)
    const photoOnEdit = photosClone[photoPos]
    photoOnEdit.file = base64ToFile(newDataUrl, photoOnEdit.fileName)
    photoOnEdit.needUpload = true
    photoOnEdit.imageUrl = newDataUrl
    setPhotos(photosClone)
  }

  return (
    <div className={['PhotoManager', compact ? 'PhotoManager_compact' : ''].filter(Boolean).join(' ')}>
      {!isEmpty(photos) && (
        <SortableList
          className="PhotoManager-PhotoGrid"
          axis="xy"
          onSortEnd={onSortEnd}
          pressDelay={200}
          items={photos.map((p, i) => (
            <PhotoTile
              key={`photoTile-${i}`}
              {...p}
              compact={compact}
              setAvatar={() => setAvatar({ photoIndex: i, photos, setPhotos })}
              onDelete={() =>
                deletePhoto({
                  photoIndex: i,
                  photos,
                  setPhotos,
                  projectId,
                  docPath: projectPath,
                  propName,
                  persistDelete: persistDeletes,
                })
              }
              onEdit={(newDataUrl) => onImageEdited(newDataUrl, i)}
            />
          ))}
        />
      )}
      <div className={['DragAndDrop-Zone'].join(' ')} {...getRootProps()}>
        <input {...getInputProps()} />
        {isDragActive ? (
          <p>Перетягніть фото сюди…</p>
        ) : compact ? (
          <p>
            Перетягніть фото або <span> оберіть файли</span>
          </p>
        ) : (
          <p>
            Перетягніть декілька фото з комп'ютера, або <span>нажміть тут</span> для вибору фото вручну
          </p>
        )}
      </div>
    </div>
  )
}

export default PhotoManager
